import { useMemo, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import type { CartItem, ItemVariant, POSItem } from '@/types';
import { useCartStore } from '@/store/cartStore';
import { formatMoney } from '@/utils/currency';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';

function getCartQtyForItem(
  items: CartItem[],
  itemName: string,
  variant?: string,
) {
  return items.reduce((sum, line) => {
    if (line.item_type !== 'Product' || line.item !== itemName) return sum;
    if (variant !== undefined && (line.variant || '') !== variant) return sum;
    return sum + line.quantity;
  }, 0);
}

function stockBadgeClass(qty: number) {
  if (qty <= 0) return 'bg-red-50 text-red-700';
  if (qty <= 5) return 'bg-amber-50 text-amber-700';
  return 'bg-emerald-50 text-emerald-700';
}

interface VariantSelectModalProps {
  item: POSItem;
  cartItems: CartItem[];
  allowOos: boolean;
  onSelect: (variant: ItemVariant) => void;
  onClose: () => void;
}

export default function VariantSelectModal({
  item,
  cartItems,
  allowOos,
  onSelect,
  onClose,
}: VariantSelectModalProps) {
  useEscapeToClose(onClose, true);
  const [search, setSearch] = useState('');
  const variants = item.variants || [];
  const customerType = useCartStore((state) => state.customerType) || 'Retailer';
  const fulfillmentType = useCartStore((state) => state.fulfillmentType) || 'Pickup';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return variants;
    return variants.filter((v) =>
      v.variant_name.toLowerCase().includes(q)
      || (v.barcode && v.barcode.toLowerCase().includes(q)),
    );
  }, [variants, search]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{item.item_name}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{variants.length} variants</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg shrink-0">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-9"
              placeholder="Search variants..."
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filtered.map((v) => {
            const stock = v.stock_quantity ?? 0;
            const inCart = getCartQtyForItem(cartItems, item.name, v.name);
            const variantOos = item.item_type === 'Product' && !allowOos && stock <= 0;
            const variantMaxed = item.item_type === 'Product' && !allowOos && inCart + 1 > stock;
            const disabled = variantOos || variantMaxed;

            const variantPrice = (() => {
              if (fulfillmentType === 'Delivery' && v.delivery_price != null && v.delivery_price > 0) {
                return v.delivery_price;
              }
              if (customerType === 'Wholesale') {
                if (v.wholesale_price != null && v.wholesale_price > 0) {
                  return v.wholesale_price;
                }
                if (v.reseller_price != null && v.reseller_price > 0) {
                  return v.reseller_price;
                }
              }
              if (customerType === 'Distributor' && v.reseller_price != null && v.reseller_price > 0) {
                return v.reseller_price;
              }
              return v.price;
            })();

            return (
              <button
                key={v.name}
                onClick={() => {
                  if (disabled) return;
                  onSelect(v);
                }}
                disabled={disabled}
                className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-lg text-sm transition-colors ${disabled
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-50 hover:bg-primary-50 border-gray-100 hover:border-primary-200'
                  }`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Plus className={`w-3.5 h-3.5 shrink-0 ${disabled ? 'text-gray-400' : 'text-primary-600'}`} />
                  <span className="text-gray-700 truncate">{v.variant_name}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="text-gray-500 text-xs">
                    {formatMoney(variantPrice)}/{v.unit_of_measurement || item.unit_of_measurement || 'unit'}
                  </span>
                  {item.item_type === 'Product' && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${stockBadgeClass(stock)}`}>
                      {stock.toLocaleString()} {v.unit_of_measurement || item.unit_of_measurement || 'pcs'}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-center text-gray-400 py-8">No variants found</p>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4">
          <button type="button" onClick={onClose} className="btn-secondary w-full">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
